import { useCallback, useEffect, useMemo, useState } from "react";
import {
  haptic,
  hideBackButton,
  hideMainButton,
  isHosted,
  setBackButton,
  setMainButton,
  telegramUser,
} from "../platform/telegram";
import {
  clearResume,
  flushPending,
  markOnboarded,
  readResume,
  saveStep,
  writeResume,
  type OnboardingProfile,
  type ProfilePatch,
} from "../api/profile";
import { BotBubble, ChipCloud, MemoryRing, SearchPick } from "./inputs";
import {
  GOAL_CHIPS,
  HOBBY_CHIPS,
  HOBBY_CHIPS_MORE,
  INTEREST_CHIPS,
  INTEREST_CHIPS_MORE,
  MAJOR_CHIPS,
  PHASES,
  REACTIONS,
  SCHOOLS,
  type PhaseKey,
} from "./data";
import "./onboarding.css";

/**
 * The onboarding flow, per "08 Onboarding" + the FOLLOWUPS §8 fix pass.
 *
 * The frame is an introduction, not a system onboarding (8.12): Froshbot
 * introduces itself first, every screen is it asking one question, answers get
 * scripted reactions, and the ending flows into the first real conversation.
 * Progress is the memory ring, never a step count. Continue is the native
 * MainButton, hidden while the keyboard owns the bottom edge (O3). Answers save
 * per step and park locally on failure (O4) — quitting never costs anything,
 * and a save failure never blocks the flow.
 */

type StepId = "name" | "school" | "major" | "interests" | "hobbies" | "goals" | "dreams";

type Screen = "hook" | "reentry" | StepId;

interface Answers {
  name: string;
  school: string | null;
  majors: string[];
  interests: string[];
  hobbies: string[];
  goals: string[];
  dreams: string;
}

const STEP_ORDER: StepId[] = ["name", "school", "major", "interests", "hobbies", "goals", "dreams"];

const STEP_PHASE: Record<StepId, PhaseKey> = {
  name: "who",
  school: "who",
  major: "who",
  interests: "fun",
  hobbies: "fun",
  goals: "want",
  dreams: "last",
};

function split(joined: string | null): string[] {
  return joined
    ? joined
        .split(", ")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
}

function answersFromProfile(profile: OnboardingProfile | null): Answers {
  return {
    name: profile?.displayName ?? telegramUser()?.first_name ?? "",
    school: profile?.school ?? null,
    majors: split(profile?.majorsMinors ?? null),
    interests: split(profile?.interests ?? null),
    hobbies: split(profile?.hobbies ?? null),
    goals: split(profile?.goals ?? null),
    dreams: profile?.dreams ?? "",
  };
}

function stepAnswered(step: StepId, a: Answers): boolean {
  switch (step) {
    case "name":
      // Prefilled from Telegram is a *default*, not an answer — the step runs.
      return false;
    case "school":
      return a.school !== null;
    case "major":
      return a.majors.length > 0;
    case "interests":
      return a.interests.length > 0;
    case "hobbies":
      return a.hobbies.length > 0;
    case "goals":
      return a.goals.length > 0;
    case "dreams":
      return a.dreams.trim().length > 0;
  }
}

/** Ring fill: committed facts over the eight the flow collects. The name step
 * always runs (a prefill is a default, not an answer), so its credit is
 * tracked separately by the flow. */
function ringFraction(a: Answers, nameCredit: boolean): number {
  const done = STEP_ORDER.filter((s) => (s === "name" ? nameCredit : stepAnswered(s, a))).length;
  return done / STEP_ORDER.length;
}

/** Receipt chips — the re-entry proof that nothing was lost. Never a count of
 * steps; always the answers themselves. */
function receiptChips(a: Answers): string[] {
  const chips: string[] = [];
  if (a.name) chips.push(`you're “${a.name}”`);
  if (a.school) chips.push(a.school);
  if (a.majors.length > 0) chips.push(a.majors[0]);
  if (a.interests.length > 0)
    chips.push(a.interests.length === 1 ? a.interests[0] : `${a.interests.length} interests`);
  if (a.hobbies.length > 0)
    chips.push(a.hobbies.length === 1 ? a.hobbies[0] : `${a.hobbies.length} hobbies`);
  if (a.goals.length > 0) chips.push(a.goals.length === 1 ? a.goals[0] : `${a.goals.length} goals`);
  if (a.dreams.trim()) chips.push("a dream");
  return chips;
}

const STEP_LABEL: Record<StepId, string> = {
  name: "your name",
  school: "school",
  major: "your major",
  interests: "the fun part",
  hobbies: "hobbies",
  goals: "goals",
  dreams: "dreams",
};

export function OnboardingFlow({
  profile,
  onComplete,
}: {
  profile: OnboardingProfile | null;
  onComplete: (answers: { name: string; goals: string }) => void;
}) {
  const [answers, setAnswers] = useState<Answers>(() => answersFromProfile(profile));
  // Dev preview (plain browser only): `?onboarding=<step>` opens the flow at
  // that step, so every screen is reachable without tapping through.
  const [screen, setScreen] = useState<Screen>(() => {
    if (isHosted) return "hook";
    const requested = new URLSearchParams(window.location.search).get("onboarding");
    return STEP_ORDER.includes(requested as StepId) ? (requested as StepId) : "hook";
  });
  const [nameCommitted, setNameCommitted] = useState(() => Boolean(profile?.displayName));
  const [resumeStep, setResumeStep] = useState<StepId | null>(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [finishFailed, setFinishFailed] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [schoolQuery, setSchoolQuery] = useState("");

  const firstName = telegramUser()?.first_name ?? profile?.displayName ?? "";
  /** Returning with answers already on file: same hook screen, one conditional
   * block — not a second flow. The copy stands alone (8.1): it never assumes
   * the user remembers where the answers came from. Only *server-saved*
   * answers count — the Telegram-name prefill is a default, not an answer, or
   * every fresh user would be greeted with "we've already started". */
  const priorAnswers = useMemo(() => {
    if (!profile) return [];
    const a = answersFromProfile(profile);
    if (!profile.displayName) a.name = "";
    return receiptChips(a);
  }, [profile]);
  const hasPrior = priorAnswers.length > 0;

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void flushPending();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Mid-flow resume: step index from CloudStorage, answers from the server
  // profile (merged with anything answered locally). Shown only mid-flow.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("onboarding")) return; // dev preview
    void readResume().then((state) => {
      if (!state) return;
      const step = STEP_ORDER.find((s) => s === state.stepId);
      if (!step || step === "name") return;
      setAnswers((current) => ({ ...current, ...(state.answers as Partial<Answers>) }));
      setResumeStep(step);
      setScreen("reentry");
    });
  }, []);

  const nextStepAfter = useCallback((from: StepId | null, a: Answers): StepId | "done" => {
    const startIndex = from === null ? 0 : STEP_ORDER.indexOf(from) + 1;
    for (let i = startIndex; i < STEP_ORDER.length; i++) {
      const step = STEP_ORDER[i];
      if (!stepAnswered(step, a)) return step;
    }
    return "done";
  }, []);

  const finish = useCallback(
    async (a: Answers) => {
      setFinishing(true);
      setFinishFailed(false);
      // Summer-class fields are no longer collected (8.9); the server defaults
      // (matchmakingMode beta_general) stand untouched.
      const patch: ProfilePatch = {
        onboardingStep: "complete",
        onboardingPromptIndex: 6,
      };
      const saved = await saveStep(patch);
      setFinishing(false);
      if (!saved) {
        // The payoff is a live chat as the real user; the server gate opens on
        // onboardingStep=complete, so this one save genuinely has to land.
        setFinishFailed(true);
        return;
      }
      haptic.success();
      clearResume();
      void markOnboarded();
      onComplete({ name: a.name, goals: a.goals.join(", ") });
    },
    [onComplete],
  );

  /** Commit the current step: save in the background (never blocking the flow),
   * advance immediately, park the patch locally if the save fails. */
  const commit = useCallback(
    (step: StepId, patch: ProfilePatch, updated: Answers) => {
      haptic.tap();
      setAnswers(updated);
      setInputFocused(false);

      const next = nextStepAfter(step, updated);
      if (next === "done") {
        void finish(updated);
        return;
      }
      setScreen(next);
      writeResume({ stepId: next, answers: updated as unknown as Record<string, unknown> });
      void saveStep({ onboardingStep: "collecting", ...patch }).then((ok) => {
        if (!ok) setSyncFailed(true);
      });
    },
    [finish, nextStepAfter],
  );

  const begin = useCallback(() => {
    haptic.tap();
    const next = nextStepAfter(null, answers);
    if (next === "done") {
      void finish(answers);
      return;
    }
    setScreen(next);
  }, [answers, finish, nextStepAfter]);

  const resume = useCallback(() => {
    haptic.tap();
    if (resumeStep && !stepAnswered(resumeStep, answers)) setScreen(resumeStep);
    else begin();
  }, [answers, begin, resumeStep]);

  /* ---------------- per-screen commits ---------------- */

  const commitName = () => {
    const name = answers.name.trim();
    if (!name) return;
    setNameCommitted(true);
    commit("name", { displayName: name, onboardingPromptIndex: 1 }, { ...answers, name });
  };

  const commitSchool = (school: string) => {
    commit("school", { school }, { ...answers, school });
  };

  const commitMajor = () => {
    if (answers.majors.length === 0) return;
    commit("major", { majorsMinors: answers.majors.join(", "), onboardingPromptIndex: 2 }, answers);
  };

  const commitInterests = () => {
    if (answers.interests.length === 0) return;
    commit(
      "interests",
      { interests: answers.interests.join(", "), onboardingPromptIndex: 3 },
      answers,
    );
  };

  const commitHobbies = () => {
    if (answers.hobbies.length === 0) return;
    commit("hobbies", { hobbies: answers.hobbies.join(", "), onboardingPromptIndex: 4 }, answers);
  };

  const commitGoals = () => {
    if (answers.goals.length === 0) return;
    commit("goals", { goals: answers.goals.join(", "), onboardingPromptIndex: 5 }, answers);
  };

  const commitDreams = () => {
    const dreams = answers.dreams.trim();
    if (!dreams) return;
    commit("dreams", { dreams, onboardingPromptIndex: 6 }, { ...answers, dreams });
  };

  /* ---------------- native buttons ---------------- */

  const goBack = useCallback(() => {
    setInputFocused(false);
    setSyncFailed(false);
    if (screen === "reentry" || screen === "hook") return;
    const index = STEP_ORDER.indexOf(screen);
    if (index <= 0) {
      setScreen("hook");
      return;
    }
    setScreen(STEP_ORDER[index - 1]);
  }, [screen]);

  // Root screens show Telegram's Close; steps get its BackButton.
  useEffect(() => {
    if (screen === "hook" || screen === "reentry") {
      hideBackButton();
      return;
    }
    return setBackButton(goBack);
  }, [screen, goBack]);

  const mainButton = useMemo((): { text: string; enabled: boolean; onClick: () => void } | null => {
    if (finishing) return { text: "Saving…", enabled: false, onClick: () => {} };
    if (inputFocused) return null; // O3 — the keyboard owns the bottom edge
    const syncSuffix = online ? "" : " — will sync";
    switch (screen) {
      case "hook":
        return {
          text: hasPrior ? "Keep going" : "Let's go",
          enabled: true,
          onClick: begin,
        };
      case "reentry":
        return { text: "Pick back up", enabled: true, onClick: resume };
      case "name":
        return { text: "That's me", enabled: answers.name.trim().length > 0, onClick: commitName };
      case "school":
        return null; // tapping a result commits
      case "major":
        return {
          text: `Sounds right — next${syncSuffix}`,
          enabled: answers.majors.length > 0,
          onClick: commitMajor,
        };
      case "interests":
        return {
          text: `That's me — next${syncSuffix}`,
          enabled: answers.interests.length > 0,
          onClick: commitInterests,
        };
      case "hobbies":
        return {
          text: `Definitely — next${syncSuffix}`,
          enabled: answers.hobbies.length > 0,
          onClick: commitHobbies,
        };
      case "goals":
        return {
          text: `That's the plan — next${syncSuffix}`,
          enabled: answers.goals.length > 0,
          onClick: commitGoals,
        };
      case "dreams":
        return {
          // Telegram ends in the payoff chat; the web ends on the waitlist.
          text: isHosted ? "Done — let's talk" : "Done — no more questions",
          enabled: answers.dreams.trim().length > 0,
          onClick: commitDreams,
        };
    }
  }, [screen, answers, inputFocused, finishing, online, hasPrior, begin, resume]);

  useEffect(() => {
    if (!mainButton) {
      hideMainButton();
      return;
    }
    return setMainButton({
      text: mainButton.text,
      is_active: mainButton.enabled,
      onClick: mainButton.enabled ? mainButton.onClick : undefined,
    });
  }, [mainButton]);

  /* ---------------- rendering ---------------- */

  const fraction = ringFraction(answers, nameCommitted);
  const phase = screen !== "hook" && screen !== "reentry" ? PHASES[STEP_PHASE[screen]] : null;

  // Outside Telegram there is no native MainButton; a dev-only fallback keeps
  // the flow walkable in a plain browser. Never rendered inside Telegram.
  const devButton =
    !isHosted && mainButton ? (
      <button
        type="button"
        className="onb-devbtn"
        disabled={!mainButton.enabled}
        onClick={mainButton.onClick}
      >
        {mainButton.text}
      </button>
    ) : null;

  const offlineBanner = !online ? (
    <div className="onb-offline">
      <i className="fa-solid fa-wifi" aria-hidden="true" /> Offline — answers save here, sync when
      you're back
    </div>
  ) : null;

  const syncFailedCard = syncFailed ? (
    <div className="onb-syncfail">
      <BotBubble>
        That didn't save — my fault, not yours. Your answers are safe on this phone.
      </BotBubble>
      <div className="onb-syncfail__actions">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            void flushPending().then((ok) => setSyncFailed(!ok));
          }}
        >
          Try again
        </button>
        <button type="button" className="btn btn--secondary" onClick={() => setSyncFailed(false)}>
          Keep going, sync later
        </button>
      </div>
    </div>
  ) : null;

  if (screen === "hook") {
    return (
      <div className="onb onb--hero">
        {offlineBanner}
        <div className="onb-hero">
          <div className="onb-hero__brandrow">
            <MemoryRing fraction={hasPrior ? fraction : 0} size={72} hero />
            <img className="onb-hero__wordmark" src="/brand/wordmark.png" alt="Freshman" />
          </div>
          <h1 className="t-title onb-hero__title">
            {hasPrior
              ? `Hey${firstName ? ` ${firstName}` : ""} — we've already started.`
              : `Hey${firstName ? ` ${firstName}` : ""}, I'm Froshbot — your new Personal Assistant.`}
          </h1>
          {hasPrior ? (
            <>
              <p className="onb-hero__sub">
                Here's what I know about you so far. A few more taps and we're set — nothing gets
                asked twice.
              </p>
              <div className="onb-receipts">
                {priorAnswers.map((chip) => (
                  <span key={chip} className="onb-receipt">
                    <i className="fa-solid fa-check" aria-hidden="true" /> {chip}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="onb-hero__sub">
                College is easier with someone in your corner, so I'm here to be in yours. I'll work
                hard to keep you in class, remember your deadlines, find your people, and help you
                achieve all of the goals you came to college to achieve!
              </p>
              <p className="onb-hero__sub">
                Your turn — <strong>two minutes, mostly tapping.</strong>
              </p>
            </>
          )}
          <ul className="onb-hero__points">
            <li>
              <i className="fa-solid fa-hand-pointer" aria-hidden="true" /> I ask, you tap. Short
              and simple.
            </li>
            {isHosted && (
              <li>
                <i className="fa-brands fa-telegram" aria-hidden="true" /> No sign-up. Your Telegram
                is your ID.
              </li>
            )}
          </ul>
        </div>
        {devButton}
      </div>
    );
  }

  if (screen === "reentry") {
    return (
      <div className="onb onb--hero">
        {offlineBanner}
        <div className="onb-hero">
          <MemoryRing fraction={fraction} size={72} hero />
          <h1 className="t-title onb-hero__title">
            Welcome back{firstName ? `, ${firstName}` : ""}.
          </h1>
          <p className="onb-hero__sub">
            Everything you answered is saved — we pick up at{" "}
            <strong>{STEP_LABEL[resumeStep ?? "name"]}</strong>.
          </p>
          <div className="onb-receipts">
            {receiptChips(answers).map((chip) => (
              <span key={chip} className="onb-receipt">
                <i className="fa-solid fa-check" aria-hidden="true" /> {chip}
              </span>
            ))}
          </div>
        </div>
        {devButton}
      </div>
    );
  }

  return (
    <div className="onb">
      {offlineBanner}
      <div className="onb-head">
        <MemoryRing fraction={fraction} size={34} />
        {phase ? <span className="onb-phase t-micro">{phase}</span> : null}
      </div>

      <div className="onb-body">
        <StepBody
          screen={screen}
          answers={answers}
          setAnswers={setAnswers}
          setInputFocused={setInputFocused}
          schoolQuery={schoolQuery}
          setSchoolQuery={setSchoolQuery}
          commitSchool={commitSchool}
        />
        {syncFailedCard}
        {finishFailed ? (
          <div className="onb-syncfail">
            <BotBubble>
              I couldn't save that last bit, and the next part is us actually talking — so I need it
              to land. Check your connection and try once more?
            </BotBubble>
            <div className="onb-syncfail__actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  const a = answers;
                  void finish(a);
                }}
              >
                Try again
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {devButton}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step bodies                                                         */
/* ------------------------------------------------------------------ */

function StepBody({
  screen,
  answers,
  setAnswers,
  setInputFocused,
  schoolQuery,
  setSchoolQuery,
  commitSchool,
}: {
  screen: StepId;
  answers: Answers;
  setAnswers: (updater: (a: Answers) => Answers) => void;
  setInputFocused: (focused: boolean) => void;
  schoolQuery: string;
  setSchoolQuery: (q: string) => void;
  commitSchool: (school: string) => void;
}) {
  // Reciprocity beat (8.12): the most recent pick can earn a scripted one-liner
  // from Froshbot. Local state — resets naturally when the step changes.
  const [lastPick, setLastPick] = useState<string | null>(null);

  const toggle = (key: "interests" | "hobbies" | "majors" | "goals") => (chip: string) =>
    setAnswers((a) => {
      const removing = a[key].includes(chip);
      setLastPick(removing ? null : chip);
      return {
        ...a,
        [key]: removing ? a[key].filter((c) => c !== chip) : [...a[key], chip],
      };
    });

  const reaction = lastPick ? REACTIONS[lastPick] : undefined;

  switch (screen) {
    case "name":
      return (
        <>
          <BotBubble>First things first — what should I call you?</BotBubble>
          <input
            className="field onb-field"
            value={answers.name}
            maxLength={60}
            autoComplete="off"
            onChange={(e) => setAnswers((a) => ({ ...a, name: e.target.value }))}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
          <p className="t-caption onb-hint">
            {telegramUser()?.first_name
              ? "from your Telegram — tap to change it"
              : "whatever people actually call you"}
          </p>
        </>
      );

    case "school":
      return (
        <>
          <BotBubble>What school are you going to?</BotBubble>
          <SearchPick
            placeholder="Search your school…"
            results={SCHOOLS.filter((s) =>
              s.name.toLowerCase().includes(schoolQuery.trim().toLowerCase()),
            )
              .slice(0, 5)
              .map((s) => ({ primary: s.name, secondary: s.place }))}
            onQuery={setSchoolQuery}
            onPick={commitSchool}
            onFocusChange={setInputFocused}
          />
        </>
      );

    case "major":
      return (
        <>
          <BotBubble>What are you studying — or circling around?</BotBubble>
          <ChipCloud
            options={MAJOR_CHIPS}
            selected={answers.majors}
            onToggle={toggle("majors")}
            onAdd={(chip) => setAnswers((a) => ({ ...a, majors: [...a.majors, chip] }))}
            addLabel="add a major or minor…"
          />
          <p className="t-caption onb-hint">“undeclared” is a real answer — most people are</p>
        </>
      );

    case "interests":
      return (
        <>
          <BotBubble>What do you actually care about? Not the college-essay version.</BotBubble>
          <ChipCloud
            options={INTEREST_CHIPS}
            moreOptions={INTEREST_CHIPS_MORE}
            selected={answers.interests}
            onToggle={toggle("interests")}
            onAdd={(chip) => setAnswers((a) => ({ ...a, interests: [...a.interests, chip] }))}
            addLabel="your thing"
          />
          <p className="t-caption onb-hint">
            {reaction
              ? `${lastPick} — ${reaction}`
              : answers.interests.length === 0
                ? "tap what's true — I'm listening"
                : answers.interests.length < 3
                  ? `${answers.interests.length} picked — a couple more and I can start being useful`
                  : "that's a person taking shape"}
          </p>
        </>
      );

    case "hobbies":
      return (
        <>
          <BotBubble>And for fun? The 7pm-on-a-Tuesday kind.</BotBubble>
          <ChipCloud
            options={HOBBY_CHIPS}
            moreOptions={HOBBY_CHIPS_MORE}
            selected={answers.hobbies}
            onToggle={toggle("hobbies")}
            onAdd={(chip) => setAnswers((a) => ({ ...a, hobbies: [...a.hobbies, chip] }))}
            addLabel="add your own"
          />
          {reaction ? (
            <p className="t-caption onb-hint">
              {lastPick} — {reaction}
            </p>
          ) : null}
        </>
      );

    case "goals":
      return (
        <>
          <BotBubble>
            What would make this term a win for you? Pick what's true — or add your own.
          </BotBubble>
          <ChipCloud
            options={GOAL_CHIPS}
            selected={answers.goals}
            onToggle={toggle("goals")}
            onAdd={(chip) => setAnswers((a) => ({ ...a, goals: [...a.goals, chip] }))}
            addLabel="say it your way…"
          />
          {reaction ? (
            <p className="t-caption onb-hint">
              {lastPick} — {reaction}
            </p>
          ) : null}
        </>
      );

    case "dreams":
      return (
        <>
          <BotBubble>
            Last question — where's all of this going for you? Big dreams welcome.
          </BotBubble>
          <textarea
            className="field onb-field onb-field--multiline"
            value={answers.dreams}
            rows={2}
            maxLength={280}
            placeholder="med school eventually. also a dog"
            onChange={(e) => setAnswers((a) => ({ ...a, dreams: e.target.value }))}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              }
            }}
          />
        </>
      );
  }
}
